/**
 * アラート欄(設計書 §5-2。2026-09-18 指示: 「対象・発生内容・差分・操作」の4要素を出す)。
 * 通常の表と区別した専用の見た目。業務ロジックは持たない(どれを要対応にするかは呼び出し側が決める)。
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from './StatusBadge';

export type AlertSeverity = 'danger' | 'warning' | 'info';
export type AlertItem = {
  id: string;
  severity: AlertSeverity;
  /** 対象(例「A社」「原価率」) */
  target: ReactNode;
  /** 発生内容(例「目標を4.6pt超過しています」) */
  message: ReactNode;
  /** 差分(例「目標 39.5% / 実績 44.1%」) */
  diff?: ReactNode;
  /** 操作(to は画面の移動、onClick はその場の操作) */
  action?: { label: string; to?: string; onClick?: () => void };
  /** 状態の札の文字を変える(既定は 要対応/注意/情報。2026-10-15 追加。例「予定」) */
  label?: string;
  /** 主な操作の左に並べる控えめな操作(2026-10-12 追加。例「登録しない」) */
  subActions?: { label: string; onClick: () => void; disabled?: boolean }[];
};

const SEVERITY_LABEL: Record<AlertSeverity, string> = { danger: '要対応', warning: '注意', info: '情報' };

export default function AlertPanel({
  title,
  meta,
  items,
  actions,
  emptyMessage = '対応が必要な項目はありません',
  compact = false,
  selection,
}: {
  title: ReactNode;
  meta?: ReactNode;
  items: AlertItem[];
  /** 見出しの右に置く操作(全件をまとめて処理するボタンなど。2026-09-22 追加) */
  actions?: ReactNode;
  emptyMessage?: ReactNode;
  /** 詰めた表示(2026-10-12 追加): 1件を1行(対象・内容・差分を横に並べ、はみ出しは「…」)、約3件分の高さを超えたら欄の中でスクロール。下の表を広く見せたい画面で使う */
  compact?: boolean;
  /** 複数選択(2026-10-17 追加): 各項目の左にチェック、見出しに「すべて選択」。選んだ項目への操作は呼び出し側が `actions` に置く */
  selection?: { selected: ReadonlySet<string>; onChange: (next: Set<string>) => void };
}) {
  const allSelected = !!selection && items.length > 0 && items.every((a) => selection.selected.has(a.id));
  const toggle = (id: string) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  };
  return (
    <section className={`alert-panel${compact ? ' is-compact' : ''}`} aria-label={typeof title === 'string' ? title : undefined}>
      <div className="alert-panel-head">
        {selection && items.length > 0 && (
          <input
            type="checkbox"
            className="alert-select"
            aria-label="すべて選択"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && items.some((a) => selection.selected.has(a.id));
            }}
            onChange={() => selection.onChange(allSelected ? new Set() : new Set(items.map((a) => a.id)))}
          />
        )}
        <h2 className="alert-panel-title">{title}</h2>
        <span className="alert-panel-count">{items.length}件</span>
        {meta && <span className="alert-panel-meta">{meta}</span>}
        {actions && <div className="alert-panel-actions">{actions}</div>}
      </div>
      {items.length === 0 ? (
        <div className="alert-panel-empty">
          <StatusBadge kind="success" label="対応なし" />
          <span>{emptyMessage}</span>
        </div>
      ) : (
        <ul className="alert-list">
          {items.map((a) => (
            <li key={a.id} className={`alert-item sev-${a.severity}${selection ? ' is-selectable' : ''}${selection?.selected.has(a.id) ? ' is-selected' : ''}`}>
              {selection && (
                <input type="checkbox" className="alert-select" aria-label={`${a.id} を選ぶ`} checked={selection.selected.has(a.id)} onChange={() => toggle(a.id)} />
              )}
              <StatusBadge kind={a.severity} label={a.label ?? SEVERITY_LABEL[a.severity]} />
              <div className="alert-item-body">
                <div className="alert-item-line">
                  <span className="alert-item-target">{a.target}</span>
                  <span className="alert-item-message">{a.message}</span>
                </div>
                {a.diff && <div className="alert-item-diff">{a.diff}</div>}
              </div>
              {(a.action || a.subActions?.length) && (
                <div className="alert-item-actions">
                  {a.subActions?.map((s) => (
                    <button key={s.label} type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={s.onClick} disabled={s.disabled}>
                      {s.label}
                    </button>
                  ))}
              {a.action &&
                (a.action.to ? (
                  <Link className="ui-btn ui-btn-secondary ui-btn-sm alert-item-action" to={a.action.to}>
                    {a.action.label}
                  </Link>
                ) : (
                  <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm alert-item-action" onClick={a.action.onClick}>
                    {a.action.label}
                  </button>
                ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
